"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { WriterAddComment } from "./WriterAddComment";
import { WriterThreadComment } from "./WriterThreadComment";
import { useWriterThreadsContext } from "./WriterThreadsContext";

interface Position {
  top: number;
  left: number;
  placement: "above" | "below";
}

export const WriterThreadPopup = () => {
  const { comments, selectedThreadId, thread, resolveThread, selectThread } =
    useWriterThreadsContext();
  const [position, setPosition] = useState<Position | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleResolveThread = () => {
    if (selectedThreadId) {
      resolveThread(selectedThreadId);
      selectThread(null); // Close the popup after resolving
    }
  };

  // Initial positioning effect - calculates position based on thread span only
  useEffect(() => {
    if (!selectedThreadId) {
      setPosition(null);
      setIsVisible(false);
      return;
    }

    const threadSpan = document.querySelector(
      `[data-thread-id="${selectedThreadId}"]`,
    );

    if (!threadSpan) return;

    const spanRect = threadSpan.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Initial positioning without popup dimensions (estimate 350px width, 400px height)
    const estimatedWidth = 350;
    const estimatedHeight = 400;

    // Calculate horizontal position (center on the span)
    let left = spanRect.left + spanRect.width / 2 - estimatedWidth / 2;

    // Ensure popup doesn't go off-screen horizontally
    const padding = 16;
    if (left < padding) {
      left = padding;
    } else if (left + estimatedWidth > viewportWidth - padding) {
      left = viewportWidth - estimatedWidth - padding;
    }

    // Calculate vertical position
    const spaceAbove = spanRect.top;
    const spaceBelow = viewportHeight - spanRect.bottom;

    let top: number;
    let placement: "above" | "below";

    if (spaceAbove > estimatedHeight + 16 && spaceAbove > spaceBelow) {
      // Place above
      top = spanRect.top - estimatedHeight - 8;
      placement = "above";
    } else {
      // Place below
      top = spanRect.bottom + 8;
      placement = "below";
    }

    setPosition({ top, left, placement });
    setIsVisible(true);
  }, [selectedThreadId]);

  // Refined positioning effect - fine-tunes position after component is rendered
  useEffect(() => {
    if (!isVisible || !position || !selectedThreadId) return;

    const refinePosition = () => {
      const threadSpan = document.querySelector(
        `[data-thread-id="${selectedThreadId}"]`,
      );

      if (!threadSpan || !popupRef.current) return;

      const spanRect = threadSpan.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Calculate horizontal position (center on the span)
      let left = spanRect.left + spanRect.width / 2 - popupRect.width / 2;

      // Ensure popup doesn't go off-screen horizontally
      const padding = 16;
      if (left < padding) {
        left = padding;
      } else if (left + popupRect.width > viewportWidth - padding) {
        left = viewportWidth - popupRect.width - padding;
      }

      // Calculate vertical position
      const spaceAbove = spanRect.top;
      const spaceBelow = viewportHeight - spanRect.bottom;
      const popupHeight = popupRect.height;

      let top: number;
      let placement: "above" | "below";

      if (spaceAbove > popupHeight + 16 && spaceAbove > spaceBelow) {
        // Place above
        top = spanRect.top - popupHeight - 8;
        placement = "above";
      } else {
        // Place below
        top = spanRect.bottom + 8;
        placement = "below";
      }

      setPosition({ top, left, placement });
    };

    // Refine position after initial render
    const timer = requestAnimationFrame(refinePosition);

    // Recalculate on scroll and resize
    window.addEventListener("scroll", refinePosition, true);
    window.addEventListener("resize", refinePosition);

    return () => {
      cancelAnimationFrame(timer);
      window.removeEventListener("scroll", refinePosition, true);
      window.removeEventListener("resize", refinePosition);
    };
  }, [isVisible, position, selectedThreadId]);

  if (!thread || !isVisible || !position) return null;

  return (
    <div
      ref={popupRef}
      className="border-foreground-3 bg-background fixed z-50 max-h-[400px] w-[350px] overflow-y-auto rounded border shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        transition: "opacity 0.1s ease-out",
      }}
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-3">
          <h3 className="text-xs font-medium text-gray-900">
            {comments.length} comment{comments.length === 1 ? "" : "s"}
          </h3>
          <Button
            onClick={handleResolveThread}
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 text-xs"
          >
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
            Resolve
          </Button>
        </div>

        {/* Comments */}
        <div className="flex-1 overflow-y-auto">
          {comments.map((comment) => (
            <WriterThreadComment key={comment.id} comment={comment} />
          ))}
        </div>

        {/* Add Comment Form */}
        <WriterAddComment threadId={selectedThreadId!} />
      </div>
    </div>
  );
};
